import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index.tsx";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { AdminRoute } from "@/components/AdminRoute";
import { RouterErrorBoundary } from "@/components/RouterErrorBoundary";
import { DuplicateRouterDetector } from "@/components/DuplicateRouterDetector";

const Auth = lazy(() => import("./pages/Auth.tsx"));
const Dashboard = lazy(() => import("./pages/Dashboard.tsx"));
const Onboarding = lazy(() => import("./pages/Onboarding.tsx"));
const Badges = lazy(() => import("./pages/Badges.tsx"));
const Pricing = lazy(() => import("./pages/Pricing.tsx"));
const AdminSimulate = lazy(() => import("./pages/AdminSimulate.tsx"));
const AdminUsers = lazy(() => import("./pages/AdminUsers.tsx"));
const AdminPayments = lazy(() => import("./pages/AdminPayments.tsx"));
const AdminWebhooks = lazy(() => import("./pages/AdminWebhooks.tsx"));
const AdminCompliance = lazy(() => import("./pages/AdminCompliance.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Refund = lazy(() => import("./pages/Refund.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const Welcome = lazy(() => import("./pages/Welcome.tsx"));
const Coaches = lazy(() => import("./pages/Coaches.tsx"));
const Stories = lazy(() => import("./pages/Stories.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <RouterErrorBoundary>
        <BrowserRouter>
          <DuplicateRouterDetector source="src/App.tsx" />
          <AuthProvider>
            <PaymentTestModeBanner />
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/coaches" element={<Coaches />} />
                <Route path="/stories" element={<Stories />} />
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute allowWithoutPillar>
                      <Onboarding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/badges"
                  element={
                    <ProtectedRoute>
                      <Badges />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/simulate"
                  element={
                    <AdminRoute>
                      <AdminSimulate />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <AdminRoute>
                      <AdminUsers />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/payments"
                  element={
                    <AdminRoute>
                      <AdminPayments />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/webhooks"
                  element={
                    <AdminRoute>
                      <AdminWebhooks />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/compliance"
                  element={
                    <AdminRoute>
                      <AdminCompliance />
                    </AdminRoute>
                  }
                />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/refund" element={<Refund />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/welcome"
                  element={
                    <ProtectedRoute allowWithoutPillar>
                      <Welcome />
                    </ProtectedRoute>
                  }
                />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </RouterErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
